/** Mirrors apps/worker/src/modules/commerce/dto/admin-order-dto.ts -- kept as a separate frontend-owned type, same rationale as every other admin feature type (see catalog/types/catalog.ts). */

export const ORDER_STATUSES = [
  'pending_payment',
  'confirmed',
  'processing',
  'out_for_delivery',
  'delivered',
  'cancelled',
  'refunded',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PAYMENT_STATUSES = ['pending', 'authorized', 'captured', 'failed', 'refunded', 'partially_refunded'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_METHODS = ['razorpay', 'cod'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export interface Money {
  amountPaise: number;
  currency: string;
}

export interface AdminOrderSummary {
  orderNumber: string;
  customerName: string | null;
  customerEmail: string;
  itemCount: number;
  subtotal: Money;
  discount: Money;
  deliveryFee: Money;
  tax: Money;
  total: Money;
  status: string;
  paymentStatus: string | null;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOrderItem {
  productName: string;
  variantName: string;
  sku: string;
  quantity: number;
  unitPrice: Money;
  lineSubtotal: Money;
  lineDiscount: Money;
  lineTax: Money;
  lineTotal: Money;
}

export interface AdminOrderStatusHistoryEntry {
  fromStatus: string | null;
  toStatus: string;
  note: string | null;
  changedAt: string;
}

export interface AdminPaymentAttempt {
  id: string;
  attemptNumber: number;
  status: string;
  razorpayPaymentId: string | null;
  errorCode: string | null;
  errorDescription: string | null;
  attemptedAt: string;
}

export interface AdminRefund {
  id: string;
  razorpayRefundId: string | null;
  amountPaise: number;
  reason: string | null;
  status: string;
  initiatedAt: string;
  processedAt: string | null;
}

export interface AdminOrderPayment {
  detail: {
    id: string;
    orderId: string;
    method: string;
    status: string;
    amountPaise: number;
    razorpayPaymentId: string | null;
    razorpayOrderId: string | null;
    capturedAt: string | null;
    failureReason: string | null;
    createdAt: string;
    updatedAt: string;
  };
  attempts: AdminPaymentAttempt[];
  refunds: AdminRefund[];
}

export interface AdminOrderDetail extends AdminOrderSummary {
  items: AdminOrderItem[];
  deliveryAddress: unknown;
  etaMinHours: number | null;
  etaMaxHours: number | null;
  customer: { fullName: string | null; email: string; accountStatus: string };
  statusHistory: AdminOrderStatusHistoryEntry[];
  cancellation: { cancelledAt: string | null; cancelledReasonId: number | null };
  coupon: { code: string; discountApplied: Money; redeemedAt: string } | null;
  payment: AdminOrderPayment | null;
  validNextStatuses: OrderStatus[];
}

export interface InvoicePreview {
  orderNumber: string;
  orderDate: string;
  billingAddress: unknown;
  items: AdminOrderItem[];
  subtotal: Money;
  discount: Money;
  tax: Money;
  deliveryFee: Money;
  total: Money;
  paymentMethod: string;
  paymentStatus: string | null;
}

export interface PackingSlip {
  orderNumber: string;
  deliveryAddress: unknown;
  items: Array<{ productName: string; variantName: string; sku: string; quantity: number }>;
}

export interface AdminOrderDetailResponse {
  order: AdminOrderDetail;
  invoice: InvoicePreview;
  packingSlip: PackingSlip;
}

export interface OrderListParams {
  search?: string;
  status?: OrderStatus[];
  paymentStatus?: PaymentStatus[];
  paymentMethod?: PaymentMethod[];
  dateFrom?: string;
  dateTo?: string;
  minTotalPaise?: number;
  maxTotalPaise?: number;
  sortField?: 'placedAt' | 'totalPaise' | 'orderNumber';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ChangeOrderStatusInput {
  status: OrderStatus;
  note?: string;
  reason?: string;
}

export interface CancelOrderInput {
  reasonId: number;
  note?: string;
}

export interface CancellationReason {
  id: number;
  code: string;
  description: string | null;
}
